import { useEffect, useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import type { StepReplayTarget } from '@/features/votes/stepReplayTarget';
import { useMakeStepReplayEval } from '@/features/votes/useMessageVotes';

interface StepReplayEvalDialogProps {
  voteId: number;
  target: StepReplayTarget;
  // the user's own explanation for the downvote; shown so the curator can write defects
  // against it, and attached to the case server-side regardless
  voteReasoning: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Collects the two curator fields and posts the case. Everything else the case needs (the
// frozen request, the recorded reply, vote reasoning, tags, introspection report) is gathered
// server-side from the vote, so the dialog only has to say which step it is about to freeze.
export function StepReplayEvalDialog({
  voteId,
  target,
  voteReasoning,
  open,
  onOpenChange,
}: StepReplayEvalDialogProps) {
  const toast = useToast();
  const makeStepReplayEval = useMakeStepReplayEval(voteId);
  const { reset } = makeStepReplayEval;
  const [responseDefects, setResponseDefects] = useState('');
  const [curatorCritique, setCuratorCritique] = useState('');

  useEffect(() => {
    if (open) return;
    reset();
    setResponseDefects('');
    setCuratorCritique('');
  }, [open, reset]);

  function handleCreate() {
    makeStepReplayEval.mutate(
      {
        response_defects: responseDefects
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
        curator_critique: curatorCritique.trim(),
      },
      {
        onSuccess: (result) => {
          onOpenChange(false);
          toast.success(
            `Step replay eval case ${result.case_id} created`,
            result.warnings.join(' · ') || undefined
          );
        },
        onError: (mutationError) => {
          toast.error(
            'Failed to create step replay eval case',
            mutationError instanceof Error ? mutationError.message : undefined
          );
        },
      }
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Make Step Replay Eval</DialogTitle>
          <DialogDescription>
            Freezes the request behind the downvoted turn&apos;s final step
            together with the reply it produced, and uploads the case to the
            eval case store. Replays re-issue that exact request with tools
            frozen. The vote reasoning, tags, and introspection report are
            attached automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Replayed step</p>
            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              Turn {target.turnNumber} · step {target.stepIndex + 1} of{' '}
              {target.stepCount}
              {target.model && (
                <span className="text-muted-foreground"> · {target.model}</span>
              )}
              {target.stepCount > 1 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Earlier steps called tools; their results are already inside
                  the frozen request and are not re-run.
                </p>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Recorded final response</p>
            <div className="max-h-40 overflow-y-auto rounded-md border bg-muted/30 p-3">
              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                {target.recordedFinalResponse || (
                  <span className="text-muted-foreground">
                    (no visible text)
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Vote reasoning</p>
            <div className="max-h-40 overflow-y-auto rounded-md border bg-muted/30 p-3">
              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                {voteReasoning?.trim() || (
                  <span className="text-muted-foreground">
                    (the user did not explain the downvote)
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="step-replay-eval-defects"
              className="text-sm font-medium"
            >
              Response defects (one per line)
            </label>
            <p className="text-xs text-muted-foreground">
              Read by the judge, which does not see the tool results. For each
              defect, say what the agent got wrong and the fact that shows it.
            </p>
            <Textarea
              id="step-replay-eval-defects"
              placeholder="e.g. The agent said the meeting is at 3pm, but the calendar result shows it is at 2pm."
              value={responseDefects}
              onChange={(e) => setResponseDefects(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="step-replay-eval-critique"
              className="text-sm font-medium"
            >
              Curator critique
            </label>
            <p className="text-xs text-muted-foreground">
              Also read by the judge. Broader problems that are not single
              checkable facts.
            </p>
            <Textarea
              id="step-replay-eval-critique"
              placeholder="e.g. Too long for a yes/no question; switched to formal English although the user wrote in casual Spanish."
              value={curatorCritique}
              onChange={(e) => setCuratorCritique(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={
              makeStepReplayEval.isPending ||
              (!responseDefects.trim() && !curatorCritique.trim())
            }
          >
            {makeStepReplayEval.isPending ? 'Creating…' : 'Create Case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
